"use client";

import { Text, SectionCard, CardTitle, CardDescription, Section } from "@/src/components";

interface TypeScaleRowProps {
  name: string;
  token: string;
  className: string;
  sampleText: string;
  details: string;
}

function TypeScaleRow({ name, token, className, sampleText, details }: TypeScaleRowProps) {
  return (
    <div className="border-b last:border-b-0 py-6 first:pt-0 last:pb-0">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex flex-col gap-1">
          <Text className="font-semibold">{name}</Text>
          <Text className="text-xs text-muted-foreground font-mono">{token}</Text>
        </div>
        <Text className="text-xs text-muted-foreground">{details}</Text>
      </div>
      <Text className={className}>{sampleText}</Text>
    </div>
  );
}

const typeScale = [
  {
    name: "Heading 1",
    token: "--h1-font-size",
    className: "text-h1 font-bold",
    sampleText: "The quick brown fox",
    details: "3rem / 4.5rem • 700",
  },
  {
    name: "Heading 2",
    token: "--h2-font-size",
    className: "text-h2 font-bold",
    sampleText: "The quick brown fox jumps",
    details: "2.25rem / 3.375rem • 700",
  },
  {
    name: "Heading 3",
    token: "--h3-font-size",
    className: "text-h3 font-semibold",
    sampleText: "The quick brown fox jumps over the lazy dog",
    details: "1.875rem / 2.8125rem • 600",
  },
  {
    name: "Heading 4",
    token: "--h4-font-size",
    className: "text-h4",
    sampleText: "The quick brown fox jumps over the lazy dog",
    details: "1.5rem / 2.25rem • 400",
  },
  {
    name: "Heading 5",
    token: "--h5-font-size",
    className: "text-h5 font-semibold",
    sampleText: "The quick brown fox jumps over the lazy dog near the riverbank",
    details: "1.25rem / 1.875rem • 600",
  },
  {
    name: "Heading 6",
    token: "--h6-font-size",
    className: "text-h6",
    sampleText: "The quick brown fox jumps over the lazy dog near the riverbank on a sunny afternoon",
    details: "1rem / 1.5rem • 400",
  },
  {
    name: "Body",
    token: "--body-font-size",
    className: "text-body",
    sampleText: "The quick brown fox jumps over the lazy dog. This is the default text size used for body copy, paragraphs, and general content throughout the application.",
    details: "0.875rem / 1.2rem • 400",
  },
  {
    name: "Caption",
    token: "--caption-font-size",
    className: "text-caption",
    sampleText: "The quick brown fox jumps over the lazy dog. Captions are used for labels, hints, metadata, and small annotations.",
    details: "0.75rem / 0.95rem • 400",
  },
];

const fontWeights = [
  { name: "Regular", value: "400", className: "font-normal" },
  { name: "Medium", value: "500", className: "font-medium" },
  { name: "Semibold", value: "600", className: "font-semibold" },
  { name: "Bold", value: "700", className: "font-bold" },
];

export default function DocsTokensTypography() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <Text as="h1" className="text-h2 font-bold">
            Typography Tokens
          </Text>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            A consistent type scale ensures visual hierarchy and readability across the
            interface. The scale uses Satoshi as the primary font family.
          </Text>
        </div>
      </Section>

      {/* Type Scale */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Type Scale</CardTitle>
            <CardDescription>Complete hierarchy from H1 to Caption</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        {typeScale.map((type) => (
          <TypeScaleRow key={type.token} {...type} />
        ))}
      </SectionCard>
      </Section>

      {/* Font Weights */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Font Weights</CardTitle>
            <CardDescription>Available weight variants</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {fontWeights.map((weight) => (
            <div key={weight.value} className="space-y-3">
              <Text className={`text-2xl ${weight.className}`}>Aa</Text>
              <div className="flex flex-col ">
                <Text className="text-sm font-medium">{weight.name}</Text>
                <Text className="text-xs text-muted-foreground font-mono">
                  {weight.value}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
      </Section>

      {/* Font Family */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Font Family</CardTitle>
            <CardDescription>Primary typeface</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="space-y-4">
          <div className="flex items-baseline gap-4">
            <Text className="text-5xl font-bold">Satoshi</Text>
            <Text className="text-muted-foreground">Primary font</Text>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-1 flex flex-col gap-1">
              <Text className="font-medium">Characters:</Text>
              <Text className="font-mono text-muted-foreground">
                ABCDEFGHIJKLMNOPQRSTUVWXYZ
              </Text>
              <Text className="font-mono text-muted-foreground">
                abcdefghijklmnopqrstuvwxyz
              </Text>
              <Text className="font-mono text-muted-foreground">0123456789</Text>
            </div>
            <div className="space-y-1">
              <Text className="font-medium">Special:</Text>
              <Text className="font-mono text-muted-foreground">
                <div>!@#$%^&*()_+-=[]{}|;':&quot;,./?</div>
              </Text>
            </div>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Usage */}
      <Section>
        <SectionCard
        header={<CardTitle>Usage</CardTitle>}
        contentClassName="p-6 space-y-4"
      >
        <div className="space-y-3">
          <Text className="font-semibold">In Tailwind:</Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{"<h1 className=\"text-h1 font-bold\">Page Title</h1>"}</code>
            <code className="block">{"<h2 className=\"text-h3 font-semibold\">Section</h2>"}</code>
            <code className="block">{"<p className=\"text-body\">Body text</p>"}</code>
            <code className="block">{"<div className=\"text-caption text-muted-foreground\">Hint</div>"}</code>
          </div>
        </div>

        <div className="space-y-3">
          <Text className="font-semibold">In CSS:</Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{".title {"}</code>
            <code className="block pl-4">{"font-size: var(--h1-font-size);"}</code>
            <code className="block pl-4">{"line-height: var(--h1-line-height);"}</code>
            <code className="block pl-4">{"font-weight: var(--h1-font-weight);"}</code>
            <code className="block">{"}"}</code>
          </div>
        </div>

        <div className="space-y-3">
          <Text className="font-semibold">With Text Component:</Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm space-y-1">
            <code className="block">{"<Text as=\"h1\" className=\"text-h1 font-bold\">"}</code>
            <code className="block pl-4">{"Page Title"}</code>
            <code className="block">{"</Text>"}</code>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Guidelines */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard
          header={<CardTitle className="text-success">✓ Do</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            <li>• Use the type scale consistently</li>
            <li>• Maintain visual hierarchy with heading levels</li>
            <li>• Use text-body for most content</li>
            <li>• Use text-caption for metadata and hints</li>
            <li>• Apply font-semibold for emphasis</li>
          </ul>
        </SectionCard>

        <SectionCard
          header={<CardTitle className="text-destructive">✗ Don't</CardTitle>}
          contentClassName="p-6"
        >
          <ul className="space-y-2 text-sm">
            <li>• Don't skip heading levels (H1 → H3)</li>
            <li>• Don't use arbitrary font sizes</li>
            <li>• Don't rely on bold alone for hierarchy</li>
            <li>• Don't use all caps for body text</li>
            <li>• Don't mix font families</li>
          </ul>
        </SectionCard>
        </div>
      </Section>
    </>
  );
}
